/**
 * `cust-list` — the customer directory and search, wired to `GET /v1/admin/customers`.
 *
 * This is the screen support opens when somebody rings. Until go-wallet#425 it
 * rendered the design corpus and badged itself as corpus, which was honest and
 * useless.
 *
 * # The route the backend took is the headline, not a footnote
 *
 * The backend dispatches on the SHAPE of the term: digits are an id, an `@` is
 * an email, an IBAN pattern is an account number, and anything else is a name.
 * Four of those five are exact index lookups and only the name route is fuzzy.
 * That distinction is the difference between two sentences an operator says out
 * loud on a phone call:
 *
 *   - exact and empty  → "no customer holds that id" — a fact about the table.
 *   - fuzzy and empty  → "nobody is spelled that way" — a fact about the query.
 *
 * A screen rendering both as an empty list invites the first sentence when only
 * the second is true, and the operator tells a real customer they do not exist.
 * So `matched_by` and `search_note` render unconditionally, above the rows,
 * whether or not there are any.
 *
 * # A route this console does not know claims nothing
 *
 * `matched_by` is a closed set today and will not stay one — the backend's own
 * `searchNote` has a default branch for exactly that reason, and it refuses to
 * describe an unknown route as exact. This module takes the same posture and
 * goes one step further: it will only call a result exact when the route is one
 * it knows to be exact AND the payload's `exact` flag agrees. "Exact and empty"
 * is the strongest claim this screen can make, and a claim nobody made must not
 * come out of a disagreement between two fields.
 *
 * # There are no balances here, deliberately
 *
 * The design corpus draws a `Σ balance` column. The live endpoint sends no
 * money at all, and law 3 requires an available balance to render as a min() of
 * two legs — this payload supplies neither. So the column is absent with a
 * stated reason rather than present and zero-filled, because a directory of
 * customers all showing €0.00 is the single most plausible-looking wrong screen
 * this console could draw.
 *
 * # No mutating control
 *
 * `restrict` / `restore` exist on the backend and are separately authorised
 * (`PermAccountRestrict`, Ops). They are out of scope for #42 by decision: this
 * is the read side, and a control belongs in a change that can argue for it.
 * Nothing here renders an action.
 */

import type {
  Block, Cell, ChipCell, Column, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'
import { exactMinorBig } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

// ---------------------------------------------------------------------------
// The wire shape — mirroring the json tags on `internal/server/admin_customers.go`
// ---------------------------------------------------------------------------

/**
 * The keyset page descriptor, shared across the admin read surface.
 *
 * `next_cursor` is present only when `has_more` is true — the backend omits it
 * on a final page on purpose, because a next cursor on the last page is a
 * button the operator clicks into an empty screen.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

/**
 * One `customers` row.
 *
 * Every nullable column is optional here because it is optional on the wire,
 * and each absence is a distinction an operator acts on: no
 * `institution_subject_ref` means this person has never been submitted for a
 * decision, and no `sla_due_at` means they are not on the clock. Defaulting
 * either to a zero value would report both as facts we hold.
 *
 * Note what is NOT here and must never be added: no password material, no
 * one-time-code seed, no recovery code, no session token, and no identity
 * provider subject id. The backend deliberately does not select them
 * (CLAUDE.md §6, invariant 13), and a field appearing here would be a request
 * for the backend to start sending one.
 */
export interface CustomerView {
  /**
   * A Go `int64`. Declared as `number | string` because the backend is moving
   * int64s onto the wire as strings — a JSON number is an IEEE-754 double in
   * every JavaScript runtime, so `JSON.parse` rounds anything past 2^53-1
   * before this console can look at it. Both encodings must read identically
   * and both go through `exactMinorBig`.
   */
  readonly id: number | string
  readonly kind: string
  readonly country_code: string
  readonly legal_name: string
  readonly email: string
  readonly institution_subject_ref?: string
  readonly acceptance: string
  readonly acceptance_reason?: string
  readonly state_entered_at: string
  readonly sla_due_at?: string
  readonly created_at: string
  readonly updated_at: string
  readonly disabled_at?: string
  readonly offboarded: boolean
}

export interface CustomerSearchResponse {
  readonly generated_at: string
  /** `id`, `email`, `iban`, `account_ref`, `name`, or `all` for the directory. */
  readonly matched_by: string
  /** Whether the route was an exact index lookup. */
  readonly exact: boolean
  /** The sentence to render beside the result. Always sent. */
  readonly search_note: string
  readonly page: PageView
  readonly customers: readonly CustomerView[]
}

/** `GET /v1/admin/customers` — the directory when `q` is absent. */
export const CUSTOMER_SEARCH_PATH = '/customers'

const SOURCE = 'postgres · customers'

/**
 * Who the rows are answerable to, and it is not one party.
 *
 * The row is ours; `acceptance` is the institution's verdict as we recorded it
 * and nothing on this surface can move it (invariant 2). Naming only "our
 * ledger" in the chip would attribute the one column an operator must not
 * argue with to us.
 */
const AUTHORITY = 'operational · acceptance is the institution’s'

// ---------------------------------------------------------------------------
// The search route
// ---------------------------------------------------------------------------

/**
 * The routes this console knows, and whether each is an exact index lookup.
 *
 * A map rather than two sets, so "is this route known" and "is this route
 * exact" cannot drift apart — they are read together at every call site and a
 * route added to one list and not the other would produce a screen that claims
 * exactness for something it cannot describe.
 */
const ROUTES: Readonly<Record<string, { readonly exact: boolean; readonly noun: string }>> = {
  all: { exact: false, noun: 'the directory' },
  id: { exact: true, noun: 'an exact lookup by customer id' },
  email: { exact: true, noun: 'an exact lookup by email address' },
  iban: { exact: true, noun: 'an exact lookup by the whole account number' },
  account_ref: { exact: true, noun: 'an exact lookup by the institution’s account reference' },
  name: { exact: false, noun: 'a fuzzy match on name' },
}

type RouteRead =
  /** A route this console knows, whose `exact` flag agreed with what we know. */
  | { readonly kind: 'known'; readonly id: string; readonly exact: boolean; readonly noun: string }
  /** A route this console does not know. It describes nothing and claims nothing. */
  | { readonly kind: 'unknown'; readonly id: string }
  /**
   * A route this console knows, whose `exact` flag contradicted it.
   *
   * Its own category rather than folded into `unknown`, because the operator
   * needs a different sentence: the route is nameable and the rows below are
   * probably fine, but the one claim that depends on exactness is not safe.
   */
  | { readonly kind: 'disputed'; readonly id: string; readonly noun: string; readonly claimed: string }

/**
 * Reads `matched_by` and `exact` together, and fails closed on either failing.
 *
 * The two fields are computed from the same value in the backend, so they
 * cannot legitimately disagree — which is precisely why a disagreement must not
 * be resolved by picking one. If the payload says the fuzzy name route was
 * exact, one of the two fields is wrong and this console cannot tell which; the
 * safe reading is that the result is not exact, because the unsafe reading
 * ends with an operator declaring a customer does not exist.
 */
function readRoute(matchedBy: unknown, exact: unknown): RouteRead {
  const id = typeof matchedBy === 'string' && matchedBy !== '' ? matchedBy : '(not stated)'
  const route = typeof matchedBy === 'string' ? ROUTES[matchedBy] : undefined
  if (route === undefined) return { kind: 'unknown', id }

  // A non-boolean `exact` is a disagreement too: the field exists to be
  // branched on without parsing a sentence, and one that cannot be branched on
  // has failed at the only job it has.
  if (exact !== route.exact) {
    const claimed = typeof exact === 'boolean'
      ? (exact ? 'exact' : 'not exact')
      : 'neither — the flag was not a boolean at all'
    return { kind: 'disputed', id, noun: route.noun, claimed }
  }
  return { kind: 'known', id, exact: route.exact, noun: route.noun }
}

/** Only a route we know AND whose flag agreed lets the screen claim exactness. */
function isExact(route: RouteRead): boolean {
  return route.kind === 'known' && route.exact
}

/**
 * The note above the rows: which route answered, and how to read the answer.
 *
 * `search_note` is carried VERBATIM into the body and never paraphrased. The
 * backend writes a different sentence per route — the email one warns that the
 * index covers live customers only, the IBAN one warns that fragments are
 * deliberately not searchable — and those are the sentences that stop an
 * operator drawing the wrong conclusion. A console-side rewrite would be a
 * second copy of the search semantics, drifting from the first.
 */
/**
 * The headline block: what the backend matched on, and what that means.
 *
 * `rowCount` is what this console could READ and `unreadable` is what it
 * returned and we withheld. Both are needed, and passing only the first is the
 * defect this signature exists to prevent: rows that arrived and could not be
 * parsed would collapse into "Nothing matched", and on an exact route into
 * "the value searched for is not held" — the console telling an operator a
 * real customer does not exist, from a payload that carried them.
 */
function routeNote(
  route: RouteRead, backendNote: string, rowCount: number, unreadable: number,
): NoteBlock {
  const empty = rowCount === 0 && unreadable === 0
  const body = backendNote === ''
    ? 'The backend sent no note describing this route, so how to read the result is not stated. This console will not supply a sentence of its own: the note is where the search semantics live and inventing one here would be a second, drifting copy of them.'
    : backendNote

  if (route.kind === 'unknown') {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Search route',
      title: `The backend answered by a route this console does not know (“${route.id}”)`,
      body: `${body} Because the route cannot be described, this result is treated as NOT exact whatever it contains — an empty list below is not evidence that nobody matches, and a populated one may have been matched on something other than what was typed.`,
      ref: `matched_by=${route.id} · treated as inexact`,
    }
  }

  if (route.kind === 'disputed') {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Search route',
      title: `The payload contradicted itself about this search (${route.noun})`,
      body: `${body} The route is ${route.noun}, but the payload flagged the result as ${route.claimed}, which does not match. One of the two fields is wrong and this console cannot tell which, so the result is treated as NOT exact — an empty list below proves nothing.`,
      ref: `matched_by=${route.id} · exact flag disagreed`,
    }
  }

  if (rowCount === 0 && unreadable > 0) {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Nothing shown',
      title: `The search returned ${String(unreadable)} customer(s) and none of them could be read`,
      body: `${body} This is NOT "nothing matched". The backend found rows; this console withheld every one of them rather than state an id it cannot state exactly. Nothing on this screen is evidence about whether the person exists — the search that would answer that has not been rendered.`,
      ref: `matched_by=${route.id} · 0 shown · ${String(unreadable)} withheld`,
    }
  }

  if (empty && route.exact) {
    return {
      t: 'note',
      tone: 'a',
      kicker: 'Nothing matched',
      title: `Nothing matched, and this was ${route.noun}`,
      body: `${body} This is an exact miss: the value searched for is not held, as opposed to being spelled differently. Read the sentence above before telling anyone the customer does not exist — an exact route can still be the wrong route if the term was read as a shape the operator did not intend.`,
      ref: `matched_by=${route.id} · exact · 0 rows`,
    }
  }

  if (empty) {
    return {
      t: 'note',
      tone: 'a',
      kicker: 'Nothing matched',
      title: route.id === 'all'
        ? 'The directory came back empty'
        : `Nothing matched, and this was ${route.noun}`,
      body: `${body} This is NOT an exact miss. An empty result here is a statement about the spelling searched for, not about whether the person exists — try the customer id, the account number, or the institution’s account reference before concluding anything.`,
      ref: `matched_by=${route.id} · fuzzy · 0 rows`,
    }
  }

  return {
    t: 'note',
    tone: 'n',
    kicker: 'Search route',
    title: `Answered by ${route.noun}`,
    body,
    ref: `matched_by=${route.id} · ${route.exact ? 'exact' : 'not exact'}`,
  }
}

// ---------------------------------------------------------------------------
// Small readers
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/** A non-empty string, or nothing. An empty string is an absence, not a value. */
function text(raw: unknown): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null
}

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is not decorative. Go's zero `time.Time` marshals as
 * `0001-01-01T00:00:00Z`, which parses perfectly well into a date in the year 1
 * — and "registered 0001-01-01" reads as a timestamp rather than as the absence
 * it is, on a column an operator sorts by.
 */
function instant(raw: unknown): string | null {
  const iso = text(raw)
  if (iso === null) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return null
  return readAtStamp(at)
}

/** The screen's own stamp. Unparseable comes back raw rather than invented. */
function stamp(iso: unknown): string {
  if (typeof iso !== 'string' || iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/**
 * A customer id, stated exactly or not at all.
 *
 * `exactMinorBig` rather than `Number()`, and rather than a local copy of it.
 * A `customers.id` is a Go `int64` and this console will paste it into another
 * screen, a ticket and a phone call, so an id wrong in its last digits is worse
 * than an id we decline to show: it looks entirely plausible and routes the
 * operator to a different customer, or to none.
 *
 * The bigint reader is the right one of the pair here. Past 2^53 a JSON *number*
 * has already been rounded by `JSON.parse` before any code here runs and must be
 * refused, while a *string* can still be stated exactly — which is the whole
 * reason `exactMinorBig`'s string branch is unbounded and its number branch is
 * not. `Number("9007199254740993")` is `…992`, so checking after conversion
 * would accept precisely the value that must be refused.
 */
function idOf(raw: unknown): string | null {
  const n = exactMinorBig(raw)
  return n === null ? null : n.toString()
}

/** Groups repeated gaps so the provenance chip stays readable at 200 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) => (n === 1 ? label : `${label} (${String(n)} customers)`))
}

// ---------------------------------------------------------------------------
// State vocabularies — unknown never renders as good
// ---------------------------------------------------------------------------

/**
 * The institution's acceptance verdicts, and how each reads.
 *
 * `accepted` is the only green one and that is the point of the table. Green on
 * this column means the institution accepted the customer, which is the state
 * that lets money move — so a value this console has not learned must never
 * inherit it. An unrecognised verdict renders in attention tone carrying its
 * raw text, and is named in the screen's provenance, because an operator
 * reading a word the console cannot interpret needs to know the console cannot
 * interpret it rather than guess from a colour.
 */
const ACCEPTANCE: Readonly<Record<string, Tone>> = {
  pending: 'a',
  in_review: 'a',
  more_info_required: 'a',
  accepted: 'g',
  rejected: 'r',
}

/** The subject kinds. An unknown kind is not an individual by default. */
const KINDS = new Set(['individual', 'company'])

// ---------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------

const COLS: readonly Column[] = [
  ['ID', 'm', '92px'],
  ['Customer', 's', 'minmax(190px,1.6fr)'],
  ['Kind', 'c', '104px'],
  ['Jurisdiction', 'x', '96px'],
  ['Acceptance', 'c', '160px'],
  ['Lifecycle', 'c', '128px'],
  ['Institution subject ref', 'm', 'minmax(150px,1fr)'],
  ['SLA due', 'm', '132px'],
  ['Registered', 'm', '132px'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  /** What this row could not state, in the operator's language. */
  readonly gaps: readonly string[]
  /**
   * The customer this row IS, for opening it.
   *
   * Carried beside the cells rather than read back out of one: the identity
   * column renders a name and a formatted id together, and a request built from
   * a rendered cell would eventually address something else entirely.
   */
  readonly key: string
}

/**
 * Folds one customer into a table row, or refuses the row.
 *
 * A row is refused only when its id cannot be stated: every other column can
 * degrade to a stated absence, but an unopenable row is a row an operator
 * cannot act on, and leaving it in the list would inflate the count of
 * customers they believe they can reach.
 */
function readCustomer(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null

  const id = idOf(o['id'])
  if (id === null) return null

  const gaps: string[] = []

  const name = text(o['legal_name'])
  const email = text(o['email'])
  if (name === null) gaps.push('a customer’s legal name')
  if (email === null) gaps.push('a customer’s email address')

  const rawKind = text(o['kind'])
  const kindCell: ChipCell = rawKind === null
    ? ['not stated', 'a']
    : [rawKind, KINDS.has(rawKind) ? 'n' : 'a']
  if (rawKind !== null && !KINDS.has(rawKind)) {
    gaps.push(`how to read the subject kind “${rawKind}”`)
  }

  const country = text(o['country_code'])
  if (country === null) gaps.push('a customer’s jurisdiction')

  const rawAcceptance = text(o['acceptance'])
  const tone = rawAcceptance === null ? undefined : ACCEPTANCE[rawAcceptance]
  const acceptanceCell: ChipCell = rawAcceptance === null
    ? ['not stated', 'a']
    : [rawAcceptance, tone ?? 'a']
  if (rawAcceptance === null) {
    gaps.push('a customer’s acceptance state')
  } else if (tone === undefined) {
    gaps.push(`how to read the acceptance state “${rawAcceptance}”`)
  }

  // `offboarded` is sent rather than inferred from `disabled_at` precisely so
  // the console does not have to infer it — so a disagreement between the two
  // is a payload that has already gone wrong once, and picking a side would
  // hide it. The row says "inconsistent" and the screen names it.
  const offboarded = o['offboarded']
  const disabledAt = instant(o['disabled_at'])
  let lifecycle: ChipCell
  if (typeof offboarded !== 'boolean') {
    lifecycle = ['not stated', 'a']
    gaps.push('whether a customer is still live or offboarded')
  } else if (offboarded === (disabledAt !== null)) {
    lifecycle = offboarded ? ['offboarded', 'n'] : ['live', 'g']
  } else {
    lifecycle = ['inconsistent', 'r']
    gaps.push('a customer whose offboarded flag and disabled_at date disagree')
  }

  // Absent until the customer has been submitted to the institution for a
  // decision — which is itself the answer to "why has nothing happened", so it
  // renders as that sentence rather than as a dash.
  const subjectRef = text(o['institution_subject_ref']) ?? 'never submitted'

  // Not `instant(…) ?? 'not on the clock'`. `instant` returns null for four
  // different facts — the field was absent, it was not a string, it did not
  // parse, and it was Go's zero instant — and only the first of those is "no
  // SLA is running". The other three are a due date the backend DID send and
  // this console could not read, and rendering them as "not on the clock"
  // tells an operator triaging the onboarding queue that a customer who may
  // already be overdue is not being counted. Customer 360 distinguishes the
  // same three cases on the same field; this column now agrees with it.
  const slaRaw = o['sla_due_at']
  const slaSent = slaRaw !== undefined && slaRaw !== null && slaRaw !== ''
  const slaParsed = instant(slaRaw)
  let slaDue: string
  if (slaParsed !== null) {
    slaDue = slaParsed
  } else if (slaSent) {
    slaDue = 'due date not readable'
    gaps.push('an onboarding SLA due date the backend sent in a form this console could not read')
  } else {
    slaDue = 'not on the clock'
  }

  const registered = instant(o['created_at'])
  if (registered === null) gaps.push('a customer’s registration date')

  return {
    key: String(id),
    cells: [
      id,
      [name ?? 'name not stated', email ?? 'email not stated'],
      kindCell,
      country ?? 'not stated',
      acceptanceCell,
      lifecycle,
      subjectRef,
      slaDue,
      registered ?? 'not stated',
    ],
    gaps,
  }
}

// ---------------------------------------------------------------------------
// The page descriptor
// ---------------------------------------------------------------------------

interface PageRead {
  readonly limit: number | null
  readonly returned: number | null
  /** `null` when the backend sent no boolean — never quietly read as false. */
  readonly hasMore: boolean | null
  readonly nextCursor: string | null
}

function readPage(raw: unknown): PageRead {
  const o = asRecord(raw)
  if (o === null) return { limit: null, returned: null, hasMore: null, nextCursor: null }
  const limit = o['limit']
  const returned = o['returned']
  const more = o['has_more']
  return {
    limit: typeof limit === 'number' && Number.isSafeInteger(limit) ? limit : null,
    returned: typeof returned === 'number' && Number.isSafeInteger(returned) ? returned : null,
    hasMore: typeof more === 'boolean' ? more : null,
    // The same exact-integer reader as the id, for the same reason: a cursor is
    // an id, and a cursor wrong in its last digits pages past customers.
    nextCursor: o['next_cursor'] === undefined ? null : idOf(o['next_cursor']),
  }
}

/**
 * What the page descriptor lets the screen claim, and what it does not.
 *
 * Stated in three registers — the table footer, a note, and the provenance chip
 * — because a page showing the first 200 of 4,000 otherwise reads as the whole
 * directory, and the directory is exactly the list an operator counts when they
 * are asked how many customers we have.
 *
 * An unreadable `has_more` is never read as `false`. "We cannot tell whether
 * this is complete" and "this is complete" are different sentences and only one
 * of them is safe to act on.
 */
function pageLine(page: PageRead, shown: number): string {
  const cap = page.limit === null
    ? 'the server did not state the page cap it applied'
    : `the server applied a page cap of ${String(page.limit)}`

  if (page.hasMore === null) {
    return `${cap} · whether more customers exist beyond this page could not be read, so this list is not known to be complete`
  }
  if (!page.hasMore) {
    return `${cap} · no further page — this is every customer the search matched`
  }
  const cursor = page.nextCursor === null
    ? 'the backend sent no usable cursor for the next page'
    : `the next page continues before customer ${page.nextCursor}`
  return `${cap} · MORE CUSTOMERS EXIST beyond these ${String(shown)} · ${cursor}`
}

function truncationNote(page: PageRead, shown: number): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Truncated',
    title: 'This is not the whole result',
    body:
      `The backend reported more customers beyond this page, so the ${String(shown)} rows above are a prefix and not the answer. `
      + 'Nothing here is counted or totalled for that reason: a count of a prefix quoted as "how many customers match" is a figure that is quietly wrong, '
      + 'and on a fuzzy name search a prefix can easily exclude the person being looked for.',
    ref: pageLine(page, shown),
  }
}

// ---------------------------------------------------------------------------
// Notes that state what this screen is not
// ---------------------------------------------------------------------------

/**
 * The corpus draws facet chips and a balance column; the endpoint has neither.
 *
 * Said on the screen rather than only in this comment, because the gap between
 * the design and the wire is exactly where a future author re-adds a zero. A
 * facet bar that does not filter and a balance column that is always zero are
 * both worse than their absence.
 */
const SCOPE_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  kicker: 'What this list does not carry',
  title: 'No balances, no account states, and no filters beyond the search term',
  body:
    'The directory endpoint answers with customer records only. It sends no money at all, and an available balance must render as a min() of our ledger position and the institution’s (law 3) — this payload supplies neither leg, so there is no balance column rather than a column of zeroes. '
    + 'Account state is a property of an account and not of a customer, so it is not here either; both live on Customer 360. '
    + 'The only filter this endpoint applies is the search term, so no facet bar is drawn — chips that do not filter would claim a scope nobody set.',
  ref: 'law 3 · law 12 · #42 is the read side only',
}

/** Restrict and restore are deliberately absent, and absence needs saying. */
const NO_CONTROL_NOTE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Read only',
  title: 'Nothing on this screen changes anything',
  body:
    'There is no restrict, restore, freeze or edit control here. `frozen` is an EMI-imposed state we display and never set (law 1), and restrict/restore are separately authorised (Ops) and belong to a change that can argue for them rather than one that inherits them. '
    + 'This screen finds a customer; every action on one is taken on their own page, where the audit line can name the subject.',
  ref: 'law 1 · law 21 · #42 scope',
}

function notShownNote(gaps: readonly string[], unreadableRows: number): NoteBlock {
  const parts: string[] = []
  if (unreadableRows > 0) {
    parts.push(
      `${String(unreadableRows)} row(s) the backend sent are not listed above, because their customer id could not be stated exactly. `
      + 'A row with no trustworthy id is a row nobody can open, and an id rounded in its last digits routes an operator to a different customer — so it is withheld rather than shown.',
    )
  }
  if (gaps.length > 0) {
    parts.push(`Not stated by the backend, and therefore not stated here: ${gaps.join('; ')}.`)
  }
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Not shown',
    title: 'Some of this result could not be read',
    body: `${parts.join(' ')} Nothing missing has been substituted with a zero, a dash read as "none", or a corpus value.`,
    ref: 'absence is a state, never a substitution',
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

function isCustomerSearchResponse(payload: unknown): payload is CustomerSearchResponse {
  const o = asRecord(payload)
  return o !== null && Array.isArray(o['customers'])
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure and separate from the fetch, so every absence path — an unknown route, a
 * self-contradicting `exact` flag, a truncated page, an id past 2^53, an
 * acceptance state this console has not learned — is table-testable without a
 * client (CLAUDE.md Part III).
 */
export function composeCustomerList(response: CustomerSearchResponse): ScreenData {
  const at = stamp(response.generated_at)
  const page = readPage(response.page)
  const route = readRoute(response.matched_by, response.exact)
  const backendNote = typeof response.search_note === 'string' ? response.search_note : ''

  const rows: RowRead[] = []
  let unreadableRows = 0
  for (const raw of response.customers) {
    const row = readCustomer(raw)
    if (row === null) unreadableRows += 1
    else rows.push(row)
  }

  const truncated = page.hasMore === true
  const gaps = summarise(rows.flatMap((r) => r.gaps))

  const table: TableBlock = {
    t: 'table',
    title: 'customers',
    src: 'ledger',
    // `false`, not an empty array: the endpoint applies no facets, and a facet
    // bar over an unfiltered read claims a scope nobody set.
    facets: false,
    cols: COLS,
    rows: rows.map((r) => r.cells),
    footer: rows.length === 0
      ? `${unreadableRows === 0 ? 'no rows' : `no rows shown, ${String(unreadableRows)} withheld`} · ${isExact(route) ? 'exact route' : 'inexact route'} · read ${at === '' ? 'at an unstated time' : at}`
      : `${String(rows.length)} row(s) shown · ${pageLine(page, rows.length)}`,
    foot2: 'acceptance is the institution’s verdict — displayed, never set here',
  }

  const blocks: Block[] = [routeNote(route, backendNote, rows.length, unreadableRows), table]
  if (truncated) blocks.push(truncationNote(page, rows.length))
  if (gaps.length > 0 || unreadableRows > 0) blocks.push(notShownNote(gaps, unreadableRows))
  blocks.push(SCOPE_NOTE, NO_CONTROL_NOTE)

  // What the chip names, in the operator's language.
  //
  // Truncation belongs here for the same reason it belongs in the footer: a
  // `Live` badge over the first page of an unknown number of customers is the
  // screen claiming to be the directory. So does an undescribable route — the
  // route is the thing this screen exists to state, and a screen that cannot
  // state it has not answered for everything it draws.
  const missing: string[] = [...gaps]
  if (unreadableRows > 0) {
    missing.push(`${String(unreadableRows)} customer row(s) whose id could not be stated exactly`)
  }
  if (route.kind === 'unknown') {
    missing.push(`how to read a result matched by “${route.id}” (a route this console does not know)`)
  }
  if (route.kind === 'disputed') {
    missing.push(`whether this result is exact (matched_by “${route.id}” and the exact flag disagreed)`)
  }
  if (backendNote === '') missing.push('the backend’s description of the search route')
  if (truncated) missing.push('the customers beyond this page (the backend reported more)')
  if (page.hasMore === null) {
    missing.push('whether this page is the whole result (the backend sent no readable has_more)')
  }
  if (page.returned !== null && page.returned !== response.customers.length) {
    missing.push(
      `a reconciled row count (the page descriptor says ${String(page.returned)} rows, the payload carried ${String(response.customers.length)})`,
    )
  }
  if (at === '') missing.push('the server-side read timestamp')

  return {
    blocks,
    provenance: missing.length === 0
      ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: at }
      : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: at, missing },
    // Which customer each row is, so a row can open into what prices them.
    rowKeys: rows.map((r) => r.key),
  }
}

/**
 * The screen when the search itself could not be read.
 *
 * One note and nothing else — never the design corpus. A corpus customer list
 * is seven plausible-looking people with plausible-looking balances, on the
 * screen an operator opens to find out whether a particular person is one of
 * our customers.
 *
 * `unread`, not `partial`: the chip renders one word, and "Partial" says some
 * of this is real.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Customer directory',
      title: 'This search did not run',
      body: `${reason} No customer below is known, because there is nothing below — and that is different from the search having matched nobody. Do not read this as “no such customer”.`,
      ref: decisionRef === null ? 'nothing was read' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/**
 * The screen when the backend refused the term the operator typed.
 *
 * Separate from an outage, and this distinction is the reason it exists. A name
 * search of fewer than three characters is a 400 whose message says, in the
 * backend's own words, "this is not a report that nobody is called that" — the
 * trigram index that serves a name cannot answer a shorter pattern without
 * scanning the customer table on the pool a cash-out shares. Rendering that as
 * an outage would hide a correctable instruction, and rendering it as an empty
 * result would be the exact false statement the backend went out of its way to
 * avoid making. The message is carried VERBATIM.
 */
function refusedScreen(message: string, decisionRef: string | null): ScreenData {
  const reason = message === ''
    ? 'The backend refused the search term but sent no explanation.'
    : message
  return {
    blocks: [{
      t: 'note',
      tone: 'a',
      kicker: 'Search refused',
      title: 'The backend would not run this search',
      body: `${reason} This is a statement about the term that was typed, not about the customer table — nothing was searched, so nothing was found, and neither of those is evidence that the person does not exist.`,
      ref: decisionRef === null ? 'no rows were read' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/**
 * Reads the directory or runs a search, and never throws for an answer.
 *
 * The term is passed through unchanged and un-trimmed. The backend dispatches
 * on the shape of what was typed, so normalising here would silently change the
 * route — a trimmed trailing space is harmless, but a console that starts
 * "cleaning" a term ends up deciding between the id route and the name route,
 * which is the backend's decision and is reported back in `matched_by`.
 *
 * An empty term is a legitimate read, not an error: it is the directory,
 * newest first, and it is what this screen opens on.
 */
export function customerSearchLoader(term = ''): ScreenLoader {
  return async (api) => {
    let payload: unknown
    try {
      payload = await api.get<unknown>(
        CUSTOMER_SEARCH_PATH,
        term === '' ? {} : { query: { q: term } },
      )
    } catch (error) {
      if (error instanceof ApiError) {
        // 400 ONLY, not the whole 4xx range. The refusal screen makes a
        // definite claim about what the operator typed, and the backend has
        // exactly one refusal of the term: the three-character minimum on a
        // name search. A 404 means this build of the console is pointed at a
        // backend that does not register the route — the term is irrelevant,
        // and telling the operator otherwise puts them in a retyping loop that
        // cannot succeed instead of paging whoever owns the rollout. 405, 409
        // and 429 are the same shape of mistake. All of them fall through to
        // the unreadable screen, which reports the status and claims nothing.
        if (error.status === 400) {
          return refusedScreen(error.message, error.decisionRef)
        }
        return unreadableScreen(
          `The customer directory endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
          error.decisionRef,
        )
      }
      // An AbortError is a navigation away, not a failure to report. Let it
      // propagate so the caller's cancellation stays a cancellation.
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return unreadableScreen('The backend could not be reached.', null)
    }

    if (!isCustomerSearchResponse(payload)) {
      return unreadableScreen(
        'The customer directory endpoint returned a shape this console does not know.',
        null,
      )
    }
    return composeCustomerList(payload)
  }
}

/** The screen as it opens: the directory, newest first, no term. */
export const loadCustomerList: ScreenLoader = customerSearchLoader()
